// Issuing the comp: the request body for POST /api/vip/bonus-requests/issue and the state of the
// Issue button. The backend re-runs the checks and saves the comp to
// bi_backoffice.vip_comp_bonuses_hist; issuing again while pending corrects it, a pushed or issued one is final.
import { formatAmount, formatDateTimeUTC, storedAmount } from './format';
import { isValidAmount } from './eligible';

const tagWagering = (check) => check.proposal.wagering !== null && check.proposal.wagering !== undefined;

// issueBody sends only what the manager set; everything else comes from the backend's checks.
export const issueBody = (check, exception = {}, overrides = {}, comment = '') => {
  const body = { user_id: check.player.user_id };
  if (overrides.amount) body.amount = overrides.amount;
  if (!tagWagering(check) && overrides.wagering != null) body.wagering = overrides.wagering;
  if (comment.trim()) body.comment = comment.trim();
  if (exception.granted && exception.reason?.trim()) body.exception_reason = exception.reason.trim();
  return body;
};

const SAVED = {
  pending: { tone: 'warning', title: 'Queued for issuing' },
  pushed: { tone: 'positive', title: 'Pushed for issuing' },
  issued: { tone: 'positive', title: 'Issued' },
  cancelled: { tone: 'muted', title: 'Cancelled' },
};

function savedView(issued) {
  if (!issued) return null;
  const meta = SAVED[issued.status] || { tone: 'muted', title: issued.status };
  const added = `Added by ${issued.created_by} on ${formatDateTimeUTC(issued.created_at)}`;
  const corrected = issued.updated_by ? ` · corrected by ${issued.updated_by} on ${formatDateTimeUTC(issued.updated_at)}` : '';
  return { ...meta, title: `${meta.title}: ${formatAmount(storedAmount(issued.amount), issued.currency)}`, by: added + corrected, comment: issued.comment || '' };
}

// Lines the backend puts in front of the manager's comment (exception reason, failed checks).
const GENERATED_LINES = ['Exception granted: ', 'Issued despite failed checks'];

// splitComment separates a saved comment into the lines the backend adds in front (reasons) and
// the manager's own comment (note).
export const splitComment = (comment) => {
  const lines = (comment || '').split('\n');
  const generated = (l) => GENERATED_LINES.some((g) => l.startsWith(g));
  return { reasons: lines.filter(generated), note: lines.filter((l) => !generated(l)).join('\n').trim() };
};

// editableComment: the saved comment without the lines the backend adds in front, to prefill a
// correction (they are added again on save).
export const editableComment = (issued) => splitComment(issued?.comment).note;

// issueState: whether the comp can be issued now, the button label and why it is disabled.
export const issueState = (check, overrides = {}) => {
  const saved = savedView(check.issued);
  const base = { saved, label: check.issued?.status === 'pending' ? 'Update bonus' : 'Issue bonus' };
  if (check.issued?.status === 'issued') return { ...base, canIssue: false, hint: 'Already issued' };
  if (check.issued?.status === 'pushed') return { ...base, canIssue: false, hint: 'Already pushed for issuing' };
  if (!overrides.amount && !check.proposal.issue_amount) return { ...base, canIssue: false, hint: 'Adjust the amount first' };
  if (!tagWagering(check) && overrides.wagering == null) return { ...base, canIssue: false, hint: 'Set the wagering first' };
  return { ...base, canIssue: true, hint: '' };
};

// Issuing anyway: a comp although a check failed. There is no proposal, so the manager enters
// amount, currency and wagering, and must say why; the backend notes the failed checks.

const CURRENCY = /^[A-Z0-9]{2,10}$/;

// canOverride: any failed check can be overridden. Without an active period the backend attaches
// the comp to this week's Friday reset (one comp per period).
export const canOverride = (check) => Boolean(check?.player);

// overridePeriodNote: where the override comp is saved when the player has no active period.
export const overridePeriodNote = (check) => (check.period?.period
  ? null
  : 'No active period: the bonus is saved under this week\'s Friday reset.');

// suggestedComp: the amount and currency the deposits would earn (backend "suggested"), in the
// issue currency, or in the basis currency when there is no rate today. Null without a suggestion.
const suggestedComp = (check) => {
  const s = check.suggested;
  if (!s) return null;
  return s.issue_amount
    ? { amount: storedAmount(s.issue_amount), currency: s.issue_currency }
    : { amount: storedAmount(s.basis_amount), currency: s.basis_currency };
};

// overrideSuggestion explains the suggested amount under the override form.
export const overrideSuggestion = (check) => {
  const comp = suggestedComp(check);
  if (!comp) return null;
  const s = check.suggested;
  const deposited = formatAmount(check.deposits?.measured, check.deposits?.basis_currency);
  return `Suggested ${formatAmount(comp.amount, comp.currency)}: ${s.rate_percent}% of ${deposited} deposited `
    + `(at least ${s.min_amount} ${s.basis_currency}), rounded to the nearest ${s.round_to} ${s.basis_currency}`
    + `${issueRoundingText(s)}.`;
};

// issueRoundingText: how the suggestion was rounded in its issue currency (unless that repeats the
// basis rounding), or that the currency still needs a rule (bi_backoffice.man_vip_comp_currency_rounding).
const issueRoundingText = (s) => {
  if (!s.issue_amount) return '';
  if (!s.issue_round_to) return `. No rounding rule for ${s.issue_currency}: the amount is not rounded, contact Ziga to add one`;
  if (s.issue_currency === s.basis_currency && s.issue_round_to === s.round_to) return '';
  return `, then rounded up to ${s.issue_round_to} ${s.issue_currency}`;
};

// overrideDefaults: the form for a new override (the suggested comp, else the top deposit currency;
// tag wagering), or the saved override being corrected.
export const overrideDefaults = (check) => {
  const i = check.issued;
  if (i) return { amount: storedAmount(i.amount), currency: i.currency, wagering: i.wagering, comment: editableComment(i) };
  const w = check.wagering;
  const comp = suggestedComp(check);
  return {
    amount: comp?.amount || '',
    currency: comp?.currency || check.deposits?.by_currency?.[0]?.currency || 'USD',
    wagering: w?.status === 'determined' ? w.wagering : null,
    comment: '',
  };
};

export const overrideState = (form) => {
  let hint = '';
  if (!isValidAmount(form.amount)) hint = 'Enter a positive amount';
  else if (!CURRENCY.test(String(form.currency).trim().toUpperCase())) hint = 'Enter the currency';
  else if (form.wagering == null) hint = 'Set the wagering';
  else if (!form.comment.trim()) hint = 'Add a comment: why is it issued anyway?';
  return { canIssue: !hint, hint };
};

export const overrideBody = (check, form) => ({
  user_id: check.player.user_id,
  override: true,
  amount: form.amount,
  currency: String(form.currency).trim().toUpperCase(),
  wagering: form.wagering,
  comment: form.comment.trim(),
});

const EXCEPTION_LINE = 'Exception granted: ';
const NO_EDIT_STATE = { overrides: { amount: null, wagering: null }, exception: { granted: false, reason: '' } };

// editStateFromSaved: a pending comp's terms as the page's edit state (adjusted amount, manual
// wagering, the exception grant), so correcting it starts from what was saved, not the proposal.
export const editStateFromSaved = (check) => {
  const i = check.issued;
  const p = check.proposal;
  if (i?.status !== 'pending' || !p) return NO_EDIT_STATE;
  const currency = p.issue_amount ? p.issue_currency : p.basis_currency;
  const saved = storedAmount(i.amount);
  const adjusted = i.currency === currency && (!p.issue_amount || Number(saved) !== Number(p.issue_amount));
  const reasonLine = (i.comment || '').split('\n').find((l) => l.startsWith(EXCEPTION_LINE));
  return {
    overrides: {
      amount: adjusted ? saved : null,
      wagering: p.wagering === null || p.wagering === undefined ? i.wagering : null,
    },
    exception: reasonLine ? { granted: true, reason: reasonLine.slice(EXCEPTION_LINE.length) } : NO_EDIT_STATE.exception,
  };
};

// submitState: issueState, also blocked while the amount being typed (adjust / enter amount) is not
// valid; null means no amount is being edited.
export const submitState = (issue, amountDraft) => {
  if (!issue.canIssue || amountDraft === null || amountDraft === undefined || isValidAmount(amountDraft)) return issue;
  return { canIssue: false, hint: 'Fix the amount: positive, max 8 decimals' };
};
